import { useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { brokerQuotes } from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatCpfCnpj, formatDate } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { Modal } from '@/shared/ui/Modal';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Skeleton, SkeletonText } from '@/shared/ui/Skeleton';
import { Stepper } from '@/shared/ui/Stepper';

import {
  faltasDoPassoPf,
  passoPfValido,
  passosDaPessoaFisica,
  rascunhoPfVazio,
  type PassoDaPessoaFisica,
  type RascunhoPf,
} from './cotacaoPessoaFisica';
import {
  BloqueioDaGeracao,
  RiscoPfStep,
  SeguradoPfStep,
  TomadorPfStep,
} from './QuotePfSteps';

const linkSecundario =
  'inline-flex h-10 items-center rounded-md px-3 text-sm font-medium text-ink-600 transition-colors duration-(--duration-fast) hover:bg-ink-100';

/**
 * "Nova cotacao - Pessoa Fisica" da corretora (`/pf/nova-cotacao` do OnPoint; NCT-182 a
 * NCT-203): a cotacao do leiloeiro, em passos (tomador, segurado, risco e resumo, um
 * por rota filha `/operacoes/nova-pf/:passo`).
 *
 * O tomador pessoa fisica e cadastrado de verdade (com os dados do leiloeiro); o resto
 * fica no rascunho da tela. "Gerar cotacao" nao grava: a importancia segurada e da
 * junta comercial, e o valor da garantia por junta nao tem regra aprovada (NCT-202). A
 * tela mostra o motivo e as pendencias que o servidor devolve, e o servidor recusa a
 * cotacao de leiloeiro da corretora com 409 regra_pendente. O assistente e da
 * corretora, como no OnPoint (a seguradora recebe /error-401). Ver
 * docs/ADR/0066-cotacao-pessoa-fisica-e-leiloeiro.md.
 */
export function NewQuotePfWizard() {
  const { session } = useAuth();
  if (session?.broker_scoped !== true) {
    return (
      <div className="flex flex-col gap-5">
        <PageHeader title="Nova cotacao - Pessoa Fisica" />
        <Alert tone="info" title="Tela da corretora">
          <div className="flex flex-col gap-3">
            <p>
              A cotacao de pessoa fisica e da corretora. A seguradora abre a operacao
              pela lista de operacoes, em Nova operacao.
            </p>
            <Link
              to="/operacoes"
              className="font-medium text-brand-700 hover:underline"
            >
              Ir para a lista de operacoes
            </Link>
          </div>
        </Alert>
      </div>
    );
  }
  return <AssistenteDaPessoaFisica />;
}

function AssistenteDaPessoaFisica() {
  const { passo: passoDaRota } = useParams();
  const passo = passoPfValido(passoDaRota);
  const navigate = useNavigate();
  const [rascunho, setRascunho] = useState<RascunhoPf>(rascunhoPfVazio);
  const [cancelando, setCancelando] = useState(false);
  const alterar = useCallback((parcial: Partial<RascunhoPf>) => {
    setRascunho((atual) => ({ ...atual, ...parcial }));
  }, []);

  const opcoes = useQuery({
    queryKey: ['individual-quote-options'],
    queryFn: ({ signal }) => brokerQuotes.individualOptions(signal),
  });

  if (opcoes.isPending) {
    return (
      <div className="flex flex-col gap-5">
        <Skeleton className="h-8 w-64" />
        <Card>
          <CardBody>
            <SkeletonText lines={5} />
          </CardBody>
        </Card>
      </div>
    );
  }
  if (opcoes.isError) {
    return (
      <div className="flex flex-col gap-5">
        <PageHeader title="Nova cotacao - Pessoa Fisica" />
        <Alert tone="danger" title="Nao foi possivel abrir o assistente">
          {opcoes.error.message}
        </Alert>
      </div>
    );
  }
  const dados = opcoes.data;
  if (!dados.enabled) {
    return (
      <div className="flex flex-col gap-5">
        <PageHeader title="Nova cotacao - Pessoa Fisica" />
        <Alert tone="info" title="Cotacao de pessoa fisica indisponivel">
          <div className="flex flex-col gap-3">
            <p>{dados.disabled_reason}</p>
            <Link
              to="/operacoes"
              className="font-medium text-brand-700 hover:underline"
            >
              Voltar para cotacoes
            </Link>
          </div>
        </Alert>
      </div>
    );
  }

  const caminho = (destino: PassoDaPessoaFisica) => `/operacoes/nova-pf/${destino}`;
  const indice = passosDaPessoaFisica.findIndex((item) => item.key === passo);
  const anterior = passosDaPessoaFisica[indice - 1]?.key;
  const seguinte = passosDaPessoaFisica[indice + 1]?.key;
  const faltas = faltasDoPassoPf(passo, rascunho);

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Nova cotacao - Pessoa Fisica"
        description="Cotacao do leiloeiro: o tomador pelo CPF e a junta comercial da UF como segurado."
        actions={
          <Link to="/operacoes" className={linkSecundario}>
            Voltar para cotacoes
          </Link>
        }
      />

      <Card>
        <CardBody>
          <Stepper
            label="Passos da cotacao de pessoa fisica"
            steps={passosDaPessoaFisica.map((item) => ({
              key: item.key,
              label: item.label,
            }))}
            currentKey={passo}
            onStepClick={(destino) =>
              void navigate(caminho(destino as PassoDaPessoaFisica))
            }
          />
        </CardBody>
      </Card>

      {passo === 'tomador' ? (
        <TomadorPfStep rascunho={rascunho} alterar={alterar} opcoes={dados} />
      ) : null}
      {passo === 'segurado' ? (
        <SeguradoPfStep rascunho={rascunho} alterar={alterar} opcoes={dados} />
      ) : null}
      {passo === 'risco' ? (
        <>
          <RiscoPfStep rascunho={rascunho} alterar={alterar} opcoes={dados} />
          {dados.generation_blocked ? <BloqueioDaGeracao opcoes={dados} /> : null}
        </>
      ) : null}
      {passo === 'resumo' ? <ResumoPf rascunho={rascunho} opcoes={dados} /> : null}

      <div className="flex flex-col items-end gap-2">
        {faltas.length > 0 ? (
          <p className="text-2xs text-ink-500">{faltas.join(' ')}</p>
        ) : null}
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="danger" onClick={() => setCancelando(true)}>
            Cancelar cotacao
          </Button>
          {anterior ? (
            <Button
              variant="secondary"
              onClick={() => void navigate(caminho(anterior))}
            >
              Voltar
            </Button>
          ) : null}
          {passo === 'risco' ? (
            <>
              <Button
                variant="secondary"
                disabled={faltas.length > 0}
                onClick={() => void navigate(caminho('resumo'))}
              >
                Ver o resumo
              </Button>
              <Button
                disabled={dados.generation_blocked || faltas.length > 0}
                title={dados.generation_blocked ? dados.block_reason : undefined}
              >
                Gerar cotacao
              </Button>
            </>
          ) : seguinte ? (
            <Button
              disabled={faltas.length > 0}
              onClick={() => void navigate(caminho(seguinte))}
            >
              Avancar
            </Button>
          ) : null}
        </div>
      </div>

      <Modal
        open={cancelando}
        onClose={() => setCancelando(false)}
        title="Cancelar cotacao"
        description="Tem certeza que deseja cancelar a cotacao? Esta acao nao podera ser desfeita."
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setCancelando(false)}>
              Manter cotacao
            </Button>
            <Button variant="danger" onClick={() => void navigate('/operacoes')}>
              Cancelar
            </Button>
          </>
        }
      >
        <p className="text-sm text-ink-700">
          O rascunho da cotacao e descartado. O tomador cadastrado continua no cadastro.
        </p>
      </Modal>
    </div>
  );
}

/**
 * Passo "Resumo da cotacao" da pessoa fisica: o que o rascunho tem. "Seguir para
 * emissao" fica desligado: a cotacao nao foi gerada (NCT-202).
 */
function ResumoPf({
  rascunho,
  opcoes,
}: {
  rascunho: RascunhoPf;
  opcoes: Parameters<typeof BloqueioDaGeracao>[0]['opcoes'];
}) {
  const modalidade = opcoes.modalities.find((item) => item.id === rascunho.modality_id);
  const linha = (rotulo: string, valor: string) => (
    <div>
      <dt className="text-2xs font-semibold text-ink-500">{rotulo}</dt>
      <dd className="text-sm text-ink-900">{valor || 'Nao informado'}</dd>
    </div>
  );
  return (
    <div className="flex flex-col gap-5">
      <Card>
        <CardHeader title="Resumo da cotacao" />
        <CardBody>
          <dl className="grid gap-4 sm:grid-cols-2">
            {linha('Dados do tomador', rascunho.policyholder_label)}
            {linha(
              'CPF do tomador',
              rascunho.policyholder_document
                ? formatCpfCnpj(rascunho.policyholder_document)
                : '',
            )}
            {linha('Dados do segurado', rascunho.junta_label)}
            {linha('Endereco do segurado', rascunho.junta_address_label)}
            {linha('Modalidade', modalidade?.name ?? '')}
            {linha(
              'Vigencia',
              rascunho.coverage_start
                ? `${formatDate(rascunho.coverage_start)} a ${
                    rascunho.coverage_end ? formatDate(rascunho.coverage_end) : '-'
                  }`
                : '',
            )}
            {linha('Prazo em dias', rascunho.term_days)}
            {linha('Objeto', rascunho.subject)}
            {linha('Importancia Segurada', 'Pendente de regra (NCT-202)')}
            {linha('Premio', 'Pendente de regra (NCT-202)')}
          </dl>
        </CardBody>
      </Card>
      <BloqueioDaGeracao opcoes={opcoes} />
      <div className="flex justify-end">
        <Button disabled title={opcoes.block_reason}>
          Seguir para emissao
        </Button>
      </div>
    </div>
  );
}
